import clsx from 'clsx';
import React, { RefObject, useEffect, useState, useRef } from 'react';

import { useFieldContext, FieldErrors } from '../';
import { Label } from '../../Elements/';

type DayMonthYearProps = {
	label: string;
	required?: boolean;
	isUs: boolean; // is US locale
	hideLabels?: boolean; // optionally hide the DMY input labels
	className?: string;
	legend?: string;
	hideLegend?: boolean; // optionally hide the input legend
} & React.InputHTMLAttributes<HTMLInputElement>;

// This component is a custom date input that splits the day, month and year into separate inputs.
// Allows for better control over the input and to allow for different date formats based on locale (e.g. MM/DD/YYYY for US and DD/MM/YYYY for UK)
// Can only be used within a Form.AppField component which provides the necessary context for it to function.
// The three inputs are combined into a single date string and the form value updated with this string whenever any of the inputs are changed.
// The value stored in the form state is a single date string in the format YYYY-MM-DD which is the expected format for HTML date inputs and the format expected by the API.

export const DayMonthYearV2 = (props: DayMonthYearProps) => {
	const {
		label,
		required,
		isUs,
		hideLabels,
		disabled = false,
		className,
		name,
		legend,
		hideLegend = true,
		...inputProps
	} = props;

	const field = useFieldContext<string>();

	const [day, setDay] = useState('');
	const [month, setMonth] = useState('');
	const [year, setYear] = useState('');
	const [allTouched, setAllTouched] = useState(false); // track whether all inputs have been touched
	const dayRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);

	// we don't want to show an error message until the user has had a chance to interact with all inputs
	const isTouched = field.state.meta.isTouched;
	const hideErrors = !isTouched || !allTouched;
	const hasErrors = field.state.meta.errors.length > 0 && !hideErrors;

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Only allow numeric input and update the corresponding state for DMY based on which input was changed
		// Also automatically move to the next input when the current one is completed
		const targetName = e.target.name;
		const newVal = e.target.value.replace(/\D/g, '');
		if (targetName.includes('day')) {
			setDay(newVal);
			if (newVal.length === 2) {
				// day field completed so move to the next field based on locale
				if (isUs && yearRef.current) yearRef.current.focus();
				else if (monthRef.current) monthRef.current.focus();
			}
		} else if (targetName.includes('month')) {
			setMonth(newVal);
			if (newVal.length === 2) {
				// month field completed so move to the next field based on locale
				if (isUs && dayRef.current) dayRef.current.focus();
				else if (yearRef.current) yearRef.current.focus();
			}
		} else if (targetName.includes('year')) {
			setYear(newVal);
		}
	};

	const onBlur = () => {
		// 'Year' is the last field for all locales so we can use it to determine when to mark the entire field as touched.
		// This will trigger error messages to show if there are any validation errors
		if (year) setAllTouched(true);
	};

	useEffect(() => {
		// expected output format is YYYY-MM-DD
		const newDate = `${year}-${('0' + month).slice(-2)}-${('0' + day).slice(-2)}`;
		if (day && month && year?.length === 4) {
			// If year has been entered then we can consider the entire field as touched to show any validation errors
			setAllTouched(true);
		}
		// store the combined date string in the form state
		field.setValue(newDate);
	}, [day, month, year, name, setAllTouched, field.setValue]);

	type renderInputProps = {
		identifier: string;
		label: string;
		placeholder: string;
		value: string;
		maxLength: number;
		ref?: RefObject<HTMLInputElement | null>;
		hideLabels?: boolean;
	};

	const renderInput = (props: renderInputProps, i: number) => {
		const { label, identifier, placeholder, value, maxLength, ref } = props;
		return (
			<div key={i} className={`flex max-w-[${maxLength + 2}rem] flex-wrap`}>
				<Label
					htmlFor={`${name}-${identifier}`}
					label={label}
					className={`flex basis-full pb-2 ${hideLabels ? 'sr-only' : ''}`}
					disabled={disabled}
				/>
				<input
					{...inputProps}
					id={`${name}-${identifier}`}
					name={`${name}-${identifier}`}
					value={value}
					placeholder={placeholder}
					onChange={onChange}
					type='text'
					inputMode='numeric'
					maxLength={maxLength}
					ref={ref || null}
					onBlur={onBlur}
					disabled={disabled}
					className={clsx(
						'flex-1 min-w-0 block w-full h-11 p-3 text-base rounded-md border',
						hasErrors && isTouched
							? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
							: 'border-primaryBorder placeholder:text-disabledText focus:ring-primaryBrand focus:border-primaryBrand disabled:bg-disabledInputBackground disabled:text-disabledInputText disabled:border-disabledInputBorder disabled:cursor-not-allowed',
						className
					)}
				/>
			</div>
		);
	};
	const dateFields = [
		{
			identifier: 'day',
			label: 'Day',
			placeholder: 'DD',
			value: day,
			maxLength: 2,
			ref: dayRef,
		},
		{
			identifier: 'month',
			label: 'Month',
			placeholder: 'MM',
			value: month,
			maxLength: 2,
			ref: monthRef,
		},
		{
			identifier: 'year',
			label: 'Year',
			placeholder: 'YYYY',
			value: year,
			maxLength: 4,
			ref: yearRef,
		},
	];

	if (isUs) {
		// swap month and day for US locale
		[dateFields[0], dateFields[1]] = [dateFields[1], dateFields[0]];
	}

	return (
		<>
			<div className='flex flex-row'>
				<Label
					htmlFor={field.name}
					label={label}
					className='mb-2'
					required={required}
					disabled={disabled}
				/>
			</div>
			<fieldset role='group'>
				{legend && (
					<legend className={`${hideLegend ? 'sr-only' : 'pb-2'}`}>{legend}</legend>
				)}
				<div className='flex flex-row gap-6'>
					{dateFields.map((info, i) => {
						return renderInput({ ...info }, i) as React.ReactNode;
					})}
				</div>
			</fieldset>
			<FieldErrors meta={field.state.meta} name={field.name} hideErrors={hideErrors} />
		</>
	);
};
